import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "@/components/layout/AppShell";
import EmptyState from "@/components/EmptyState";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Bell, UserPlus, Check, CheckCheck } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import api, { mediaUrl } from "@/lib/api";
import { useRealtime } from "@/context/RealtimeContext";
import { useLanguage } from "@/context/LanguageContext";
import { logError } from "@/lib/log";

const ICONS = { friend_request: UserPlus, friend_accepted: Check, follow_request: UserPlus, follow_accepted: Check };

export default function NotificationsPage() {
  const { t } = useLanguage();
  const [items, setItems] = useState([]);
  const navigate = useNavigate();
  const { subscribe } = useRealtime();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data);
    } catch (e) {
      logError("NotificationsPage", e);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => subscribe((evt) => {
    if (["notification", "friend_request", "friend_accepted", "follow_request", "follow_accepted"].includes(evt.type)) load();
  }), [subscribe, load]);

  const markAll = async () => {
    await api.post("/notifications/read-all");
    window.dispatchEvent(new Event("yaprio:refresh-unread"));
    load();
  };

  const timeAgo = (iso) => {
    try {
      return formatDistanceToNow(new Date(iso), { addSuffix: true });
    } catch {
      return "";
    }
  };

  const openNotif = async (n) => {
    if (!n.read) {
      try {
        await api.post(`/notifications/${n.id}/read`);
        window.dispatchEvent(new Event("yaprio:refresh-unread"));
      } catch (e) {
        logError("NotificationsPage", e);
      }
    }
    navigate(n.type === "friend_request" || n.type === "follow_request" ? "/friends?tab=requests" : "/friends");
  };

  return (
    <AppShell>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">
            {t("Notifications")}
          </h1>
          <p className="mt-1 text-sm text-slate-500">{t("Friend requests and updates land here.")}</p>
        </div>
        {items.some((n) => !n.read) && (
          <Button
            variant="outline"
            onClick={markAll}
            data-testid="btn-mark-all-read"
            className="gap-2 rounded-full font-semibold"
          >
            <CheckCheck className="h-4 w-4" /> {t("Mark all read")}
          </Button>
        )}
      </div>

      <Card className="rounded-3xl border-slate-100 shadow-sm">
        {items.length === 0 ? (
          <EmptyState
            icon={Bell}
            title={t("You're all caught up")}
            description={t("When someone follows you or sends a friend request, you'll see it here.")}
            testId="empty-state-notifications"
          />
        ) : (
          <div className="divide-y divide-slate-50" data-testid="notifications-list">
            {items.map((n) => {
              const Icon = ICONS[n.type] || Bell;
              return (
                <button
                  key={n.id}
                  onClick={() => openNotif(n)}
                  data-testid={`notification-${n.id}`}
                  className={`flex w-full items-center gap-3 p-4 text-left transition-colors hover:bg-slate-50 ${
                    n.read ? "" : "bg-pink-50/50"
                  }`}
                >
                  <div className="relative">
                    <Avatar className="h-11 w-11">
                      <AvatarImage src={mediaUrl(n.actor_avatar_url)} alt={n.actor_username} />
                      <AvatarFallback className="bg-pink-500 font-bold text-white">
                        {(n.actor_username || "?").charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <span className="absolute -bottom-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-white shadow">
                      <Icon className="h-3 w-3 text-pink-500" />
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm text-slate-800">{n.message}</p>
                    <p className="text-xs text-slate-400">{timeAgo(n.created_at)}</p>
                  </div>
                  {!n.read && <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-pink-500" />}
                </button>
              );
            })}
          </div>
        )}
      </Card>
    </AppShell>
  );
}
